import { useEffect, useRef, useState } from 'preact/hooks';

/** Pixel position of the draggable element's top-left corner, viewport-relative. */
export interface Position {
  x: number;
  y: number;
}

export interface UseDraggableOptions {
  /** Suffix for the `ft:pos:<storageKey>` localStorage key the position is persisted under. */
  storageKey: string;
  /** Used only when nothing is stored yet (first-ever render for this key). */
  initialPosition?: Position;
  /** Approximate footprint of the dragged element, used to keep it inside the viewport. */
  size?: { width: number; height: number };
}

export interface DragHandlers {
  onPointerDown: (e: PointerEvent) => void;
  onPointerMove: (e: PointerEvent) => void;
  onPointerUp: (e: PointerEvent) => void;
}

export interface UseDraggableResult {
  position: Position;
  dragHandlers: DragHandlers;
  /**
   * Non-consuming peek at whether the most recently completed pointer gesture moved past
   * the click/drag threshold. Does NOT reset the flag — prefer `consumeWasDragged()` for
   * anything that acts on the result (e.g. click suppression), since a plain peek left
   * unconsumed would keep suppressing every activation after the first drag, including
   * later keyboard (Enter/Space) activations that dispatch a synthetic `click` with no
   * preceding `pointerdown`.
   */
  wasDragged: () => boolean;
  /**
   * Read-then-clear (consume-once) version of `wasDragged()`. Returns the current flag and
   * immediately resets it to `false`. Callers use this to suppress `onOpen` on the click
   * event that a browser fires immediately after a drag's `pointerup` — e.g.
   * `onClick={() => { if (!consumeWasDragged()) onOpen(); }}` — while guaranteeing that
   * exactly that one trailing click is suppressed and every activation after it (including
   * a keyboard Enter/Space press, which has no preceding `pointerdown` to reset the flag)
   * is evaluated fresh.
   */
  consumeWasDragged: () => boolean;
  /**
   * True while the button is still on the default corner. A drag, or a
   * position already in storage, clears it. `--toast-lift` is published only
   * while this is true, so a button the reporter moved does not keep lifting
   * the toast toward the corner they left.
   */
  anchored: boolean;
}

/** Total pointer movement (px) below which a gesture counts as a click, not a drag. */
const DRAG_THRESHOLD_PX = 5;

/** Fallback footprint used to keep the button on-screen when the caller doesn't pass one. */
const DEFAULT_SIZE = { width: 56, height: 56 };

/** Gap (px) kept between the default position and the viewport edge, purely cosmetic. */
const DEFAULT_MARGIN_PX = 24;

function storageKeyFor(key: string): string {
  return `ft:pos:${key}`;
}

function isPosition(value: unknown): value is Position {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as Position).x === 'number' &&
    typeof (value as Position).y === 'number'
  );
}

/**
 * Reads the persisted position for `key`. Guarded end-to-end: `localStorage` can throw
 * just by being accessed (private browsing in some browsers, storage disabled by policy),
 * not only on `.getItem()`, so the whole read is wrapped — never let this throw into the
 * widget's fail-silent boundary.
 */
function readStoredPosition(key: string): Position | null {
  try {
    const raw = window.localStorage.getItem(storageKeyFor(key));
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return isPosition(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function writeStoredPosition(key: string, position: Position): void {
  try {
    window.localStorage.setItem(storageKeyFor(key), JSON.stringify(position));
  } catch {
    // Position just won't survive a reload — never let storage failures propagate.
  }
}

function viewportSize(): { width: number; height: number } {
  return {
    width: typeof window !== 'undefined' ? window.innerWidth : Infinity,
    height: typeof window !== 'undefined' ? window.innerHeight : Infinity,
  };
}

function clampToViewport(position: Position, size: { width: number; height: number }): Position {
  const { width: vw, height: vh } = viewportSize();
  const maxX = Math.max(0, vw - size.width);
  const maxY = Math.max(0, vh - size.height);
  return {
    x: Math.min(Math.max(position.x, 0), maxX),
    y: Math.min(Math.max(position.y, 0), maxY),
  };
}

/**
 * The height of a strip the host keeps along the bottom of its window, from
 * `--fb-lane-h` on its root element: the same host-owned property the edge
 * tab already rises by. Tenger Works declares it for its hint bar, so the
 * button's default corner clears that bar instead of sitting on it (I22658).
 * Zero when the host declares nothing, or anything unreadable.
 */
function hostLaneHeight(): number {
  try {
    const raw = getComputedStyle(document.documentElement).getPropertyValue('--fb-lane-h');
    const px = parseFloat(raw);
    return Number.isFinite(px) && px > 0 ? px : 0;
  } catch {
    return 0;
  }
}

function defaultPosition(size: { width: number; height: number }): Position {
  const { width: vw, height: vh } = viewportSize();
  return {
    x: Math.max(0, vw - size.width - DEFAULT_MARGIN_PX),
    y: Math.max(0, vh - size.height - DEFAULT_MARGIN_PX - hostLaneHeight()),
  };
}

interface DragStart {
  pointerId: number;
  startX: number;
  startY: number;
  originX: number;
  originY: number;
}

/**
 * Pointer-events-based drag behavior (mouse + touch + pen via one event family) with a
 * click/drag threshold: total movement under `DRAG_THRESHOLD_PX` between pointerdown and
 * pointerup is a click and leaves position untouched; past that, it's a drag — position
 * updates live and gets clamped to the viewport and persisted to localStorage on release.
 *
 * `setPointerCapture`/`releasePointerCapture` are called best-effort (guarded — not every
 * environment implements them, notably jsdom) so drags that leave the element's bounds
 * keep tracking the pointer where supported, without being required for correctness here
 * since `pointermove`/`pointerup` are also handled directly on the element.
 */
export function useDraggable({
  storageKey,
  initialPosition,
  size = DEFAULT_SIZE,
}: UseDraggableOptions): UseDraggableResult {
  const [position, setPosition] = useState<Position>(() =>
    clampToViewport(readStoredPosition(storageKey) ?? initialPosition ?? defaultPosition(size), size),
  );

  const draggedRef = useRef(false);
  const startRef = useRef<DragStart | null>(null);
  // Whether the position is user-owned (was persisted, or has been dragged this
  // session). User-owned positions are only clamped on-screen; an un-owned
  // (default) position is re-anchored bottom-right as the real size arrives.
  const userOwnedRef = useRef(readStoredPosition(storageKey) !== null);
  const [anchored, setAnchored] = useState(() => !userOwnedRef.current);

  // Re-anchor / re-clamp when the measured `size` becomes known (the button is
  // measured after first paint) or the viewport resizes, so a wide button never
  // sits half off the edge. Depends on the primitive dims, not the object.
  useEffect(() => {
    const reflow = () => {
      setPosition((current) =>
        userOwnedRef.current
          ? clampToViewport(current, size)
          : clampToViewport(defaultPosition(size), size),
      );
    };
    reflow();
    window.addEventListener('resize', reflow);
    return () => window.removeEventListener('resize', reflow);
    // The two size fields rather than `size` itself: the object identity
    // changes on every render, and re-running this would re-clamp the
    // launcher mid-drag.
  }, [size.width, size.height]);

  const onPointerDown = (e: PointerEvent) => {
    draggedRef.current = false;
    startRef.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      originX: position.x,
      originY: position.y,
    };
    try {
      const target = e.currentTarget as (EventTarget & { setPointerCapture?: (id: number) => void }) | null;
      target?.setPointerCapture?.(e.pointerId);
    } catch {
      // Best-effort — see doc comment above.
    }
  };

  const onPointerMove = (e: PointerEvent) => {
    const start = startRef.current;
    if (!start || start.pointerId !== e.pointerId) return;
    const dx = e.clientX - start.startX;
    const dy = e.clientY - start.startY;
    if (!draggedRef.current && Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;
    draggedRef.current = true;
    setPosition(clampToViewport({ x: start.originX + dx, y: start.originY + dy }, size));
  };

  const onPointerUp = (e: PointerEvent) => {
    const start = startRef.current;
    if (!start || start.pointerId !== e.pointerId) return;
    try {
      const target = e.currentTarget as
        | (EventTarget & { releasePointerCapture?: (id: number) => void })
        | null;
      target?.releasePointerCapture?.(e.pointerId);
    } catch {
      // Best-effort — see doc comment above.
    }
    startRef.current = null;
    if (draggedRef.current) {
      // The user has placed the button — future reflows clamp it rather than
      // snapping it back to the default anchor.
      userOwnedRef.current = true;
      setAnchored(false);
      // Functional form to persist whatever the latest committed position is, without
      // depending on a closure over `position` that could be stale by the time this
      // pointerup handler runs.
      setPosition((current) => {
        writeStoredPosition(storageKey, current);
        return current;
      });
    }
  };

  const consumeWasDragged = () => {
    const wasDragged = draggedRef.current;
    draggedRef.current = false;
    return wasDragged;
  };

  return {
    position,
    dragHandlers: { onPointerDown, onPointerMove, onPointerUp },
    wasDragged: () => draggedRef.current,
    consumeWasDragged,
    anchored,
  };
}

/**
 * BUTTON launchers still use this hook. EDGE_TAB does not: that trigger is
 * fixed to the right edge. The module also stays so the citation in ADR 0016
 * (`useDraggable.ts:152`) still resolves. An accepted ADR is not edited, and
 * the docs checker treats a missing path as a hard failure.
 */
